import { Injectable, Injector } from "@angular/core";
import { collection, doc, DocumentSnapshot, Firestore, getDoc, getDocs, onSnapshot, orderBy, query } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { ModalController } from "@ionic/angular";
import { combineLatest, EMPTY, from, Observable, of, ReplaySubject, Subscription, throwError } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, switchMapTo, take, tap } from "rxjs/operators";
import { BaseService } from "src/app/app.base.service";
import { CommonService } from "src/app/services/common/common.service";
import { MarketingCampaignsService } from "src/app/services/marketing-campaigns.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { RequestSyndicateService } from "src/app/services/RequestBodyService/RequestSyndicate/request-syndicate.service";
import { SyndicateDataService } from "src/app/services/syndicate-data.service";
import { UserManagementService } from "src/app/services/user-management.service";

@Injectable({
  providedIn: "root",
})
export class SyndicateRepositoryService extends BaseService {
  private invitationId$ = new ReplaySubject(1);
  private syndicateId$ = new ReplaySubject<{ psId; psName }>(1);
  // syndicateJackpotTriggers$ = this.syndicateDataService.getSyndicateJackpotTriggers(this.userId);
  syndicateJackpotTriggers$ = new Observable<any[]>((observer) => {
    this.commonService.getUid('SyndicateRepositoryService').subscribe({
      next: (userId) => {
        if (!userId || userId === 'null') {
          observer.next([]);
          observer.complete();
          return;
        }

        this.syndicateDataService
          .getSyndicateJackpotTriggers(userId)
          .subscribe({
            next: (data: any) => {
              observer.next(data ?? []);
              observer.complete();
            },
            error: (error) => {
              if (
                error?.code === 'permission-denied' ||
                error?.message?.includes('permission')
              ) {
                // Expected permission error, log at debug level
              } else {
                this.authService.errorHandlerLogs(
                  '[SyndicateRepositoryService] Error fetching jackpot triggers:',
                  error
                );
              }
              observer.next([]);
              observer.complete();
            }
          });
      },
      error: (error) => {
        this.authService.errorHandlerLogs(
          '[SyndicateRepositoryService] Error getting userId:',
          error
        );
        observer.next([]);
        observer.complete();
      }
    });
  }).pipe(
    shareReplay(1) // Cache the result
  );

  previousCheck$ = this.authService?.previousCheck$;
  isCheckWinning$ = this.authService?.userDetails$;
  syndicateMembersSub: Subscription;
  isWinning$ = this.commonService.getUid("SyndicateRepositoryService").pipe(
    switchMap((userId) => {
      return this.userManagementService?.userRequest$?.pipe(
        switchMap((user: any) => {
          return user
            ? this.authService
              .getAngularFireStore(
                `/users/${userId}/in_app_notification/status`,
                "snapshotChanges"
              )
              .pipe(
                map((notificationData: DocumentSnapshot) => {
                  let notification = notificationData?.data() as Data;
                  if (notification) {
                    return notification?.winning;
                  }
                  return false;
                })
              )

            : of(false);



        })
      );
    })
  );

  syndicateInvitedDetails$ = this.invitationId$.pipe(
    filter((data) => this.marketingCampaignsService.getInvitationData()?.psId != null),
    switchMap((invitationId) => {
      if (!this.marketingCampaignsService.getInvitationData().psId) return EMPTY;
      const ps = this.catchService.isOnline$.pipe(
        switchMap(async (isOnline) => {
          if (!isOnline) return null;

          const psDocRef = doc(this.firestore, `private_syndicate/${this.marketingCampaignsService.getInvitationData().psId}`);
          const psSnap = await getDoc(psDocRef); // Fetch private_syndicate document
          const data = psSnap?.data();
          if (!data) {
            return null;
          }
          data["psId"] = this.marketingCampaignsService.getInvitationData().psId;
          return data;
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      );

      if (!this.marketingCampaignsService.getInvitationData().member_id) return EMPTY;
      const userData = this.catchService.isOnline$.pipe(
        switchMap(async (isOnline) => {
          if (!isOnline) return null;

          const userDocRef = doc(this.firestore, `users/${this.marketingCampaignsService.getInvitationData().member_id}`);
          const userSnap = await getDoc(userDocRef); // Fetch user document
          const data = userSnap?.data();
          if (!data) {
            return null;
          }
          data["userId"] = this.marketingCampaignsService.getInvitationData().member_id;
          return data;
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      );

      return combineLatest(ps, userData);
    })
  );

  syndicateMembers$ = this.syndicateId$.pipe(
    switchMap((ps: any) => {
      return ps?.psId
        ? this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return [];

            const collectionRef = collection(
              this.firestore,
              `/private_syndicate_member_listing/${ps?.psId}/members`
            ); // Reference Firestore collection
            const q = query(collectionRef); // Query the collection
            const querySnapshot = await getDocs(q); // Fetch documents

            return querySnapshot.docs.map((doc) => ({
              ...doc?.data(),
              memberId: doc?.id, // Add memberId
            }));
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        ).pipe(
          map((memberList) => {
            return { ...{ members: memberList }, ...{ syndicateName: ps?.psName } };
          })
        )
        : of([]);
    })
  );

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private injector: Injector,
    private catchService: CatchErrorService,
    private modalCtrl: ModalController,
    private RequestPaymentService: RequestPaymentService,
    private RequestSyndicateService: RequestSyndicateService,
    private userManagementService: UserManagementService,
    private syndicateDataService: SyndicateDataService,
    private marketingCampaignsService: MarketingCampaignsService,
    private commonService: CommonService
  ) {
    super(injector);
    if (this.syndicateMembers$) this.syndicateMembersSub = this.syndicateMembers$?.subscribe();
  }

  getLottery({ syndicate_id, syndicate_type }) {
    const moduleName = "get_tickets";
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) userId = null;
        return this.synchronizerFunction(
          this.RequestSyndicateService.getSyndicateTicketsRequest(syndicate_id, syndicate_type)
        ).pipe(
          switchMapTo(
            this.authService
              .getAngularFireStore(
                `/users/${userId}/syndicate_module/${moduleName}/details/${syndicate_id}`,
                "snapshotChanges"
              )
              .pipe(map((res: Data) => res?.payload?.data()))
          )
        );
      }));
  }

  leaveSyndicate(syndicateId, syndicateType, reason) {
    return this.RequestSyndicateService
      .getPrivateSyndicateLeaveRequest(syndicateId, syndicateType, reason)
      .pipe(
        switchMap((request) => {
          if (!request) {
            return throwError(() => new Error('Invalid request'));
          }
          return this.authService.getV2CloudFunction("apiv2", request);
        })
      );
  }

  pauseSyndicate(syndicateId, type, syndicatePauseGift) {
    return this.RequestSyndicateService.getPauseSyndicateRequest(
      syndicateId,
      type,
      syndicatePauseGift
    ).pipe(
      switchMap((requestBody) => {

        if (!requestBody) {
          return of(null);
        }

        return this.authService.getV2CloudFunction("apiv2", requestBody);
      })
    );
  }

  unPauseSyndicate(syndicateId) {
    return this.authService.getV2CloudFunction("unpausedsyndicatev2", { syndicateId: syndicateId });
    // return this.angularFireFunctions.httpsCallable("unpausedSyndicate")({ syndicateId: syndicateId });
  }
  viewPlayersCount(syndicate: any) {
    return this.RequestSyndicateService.getSyndicateSizeRequest(syndicate).pipe(
      switchMap((requestPayload) => {
        if (!requestPayload) return of(null);
        return this.authService.v2CloudFunctionResponse(
          this.callableFunction(requestPayload)
        );
      })
    );
  }

  getRaffleNum(syndicateId) {
    try {
      // const api = this.angularFireFunctions.httpsCallable("validatePostAsyncEvents");
      // return api(this.requestBodyService.getRaffleNum(syndicateId));
      return this.authService.getV2CloudFunction(
        "getrafflenumber",
        this.requestBodyService.getRaffleNum(syndicateId)
      );
    } catch (error) {
      return error;
    }
  }

  getPsMemberList(id: string) {
    if (!id) return of(null);

    return this.catchService.isOnline$.pipe(
      switchMap(async (isOnline) => {
        if (!isOnline) return [];

        const collectionRef = collection(this.firestore, `/private_syndicate_member_listing/${id}/members`); // Reference Firestore collection
        const q = query(collectionRef); // Query the collection
        const querySnapshot = await getDocs(q); // Fetch documents

        return querySnapshot.docs.map((doc) => ({
          ...doc?.data(),
          memberId: doc?.id, // Add memberId
        }));
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }

  setSyndicateMembers(psDetails: any) {
    this.syndicateId$.next(psDetails);
  }

  getSyndicateMember(id: string) {
    if (!id) return of(null);
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return [];
        return this.authService.getUid$("SyndicateRepositoryService").pipe(
          switchMap(async (userId) => {
            if (!userId) userId = null;
            const collectionRef = collection(this.firestore, `/private_syndicate_member_listing/${id}/members`); // Reference Firestore collection
            const q = query(collectionRef); // Query the collection
            const querySnapshot = await getDocs(q); // Fetch documents

            const members = querySnapshot.docs.map((doc) => ({
              ...doc?.data(),
              memberId: doc?.id, // Add memberId to the data
            }));

            return members?.find((x: any) => x?.user_id === userId); // Find the member with matching userId
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      }));
  }

  updateInvitationId(invitationId: string) {
    this.invitationId$.next(invitationId);
  }

  joinSyndicateInvitation(psId, invitedId, inviteMemberId, joined) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(
        this.RequestSyndicateService.joinSyndicateInvitation(psId, invitedId, inviteMemberId, joined)
      )
    );
  }

  /*async activateTrigger(trigger: any) {
    console.info("Activating trigger:", trigger);
    return from(this.authService?.v2CloudFunctionResponse(
      this.validatePostAsyncEvents(this.requestBodyService.activateTrigger(trigger, trigger.alias))
    )).pipe(
      catchError((error) => {
        // Handle the error here
        this.authService.errorHandlerLogs('Error activating trigger:', error);
        return throwError(error);
      })
    );
  } */

  activateTrigger(trigger: any): Observable<any> {
    const path_id = "1cblQl68QltRugb1YDAY";

    return this.authService?.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      method: "read",
    }).pipe(
      map((snapshot: Data) => snapshot?.data),
      switchMap((docSnap: Data) => {
        if (docSnap) {
          const promoDetails = docSnap?.promo_details || [];

          // Update 'activate_jackpot_trigger' status
          const updatedPromoDetails = promoDetails.map((promo: Data) =>
            promo.promo_name === "activate_jackpot_trigger"
              ? { ...promo, status: true }
              : promo
          );

          return this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data: { promo_details: updatedPromoDetails },
            method: "update",
          });
        } else {
          // Create document with default promo_details
          const defaultPromoDetails = [
            { promo_name: "activate_jackpot_trigger", status: true }
          ];

          return this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data: { promo_details: defaultPromoDetails },
            method: "create",
          });
        }
      }),
      // 🔹 BUILD ASYNC EVENT REQUEST BODY (FIXED)
      switchMap(() => {
        return this.requestBodyService.activateTrigger(
          trigger,
          trigger?.alias
        );
      }),
      // 🔹 FIRE ASYNC EVENT
      switchMap((requestBody) => {
        if (!requestBody) {
          return of(null);
        }
        return this.authService.getV2CloudFunction("updatejackpottrigger", requestBody);
      }),
      // 🔹 FINAL RESPONSE
      map((finalResponse) => {
        return finalResponse;
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("Error in activateTrigger:", error);
        return throwError(() => error);
      })
    );
}




  deactivateTrigger(trigger) {
    try {
      if (!trigger) {
        return "Trigger not found";
      }

      return this.requestBodyService.deactivateTrigger(trigger).pipe(
        switchMap((body) => {
          if (!body) {
            return from(Promise.resolve(null));
          }
          return from(
            this.authService.getV2CloudFunction("updatejackpottrigger", body)
          );
        })
      );

    } catch (error) {
      return error;
    }
  }

  inviteValidation(psId, referralUserId) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestSyndicateService.getInvitedSyndicateDataValidationRequest(psId, referralUserId))
    );
  }

  getPrivateSyndicateProducts(syndId) {
    return this.authService
      .getAngularFireStore(`/private_syndicate_ticket_listing/${syndId}`, "snapshotChanges")
      .pipe(map((res: Data) => res?.payload?.data()));
  }

  getPrivateSyndicateTickets(syndId: string) {
    if (!syndId) return of([]);

    const syndDocRef = doc(this.firestore, `private_syndicate_ticket_listing/${syndId}`);

    return from(getDoc(syndDocRef)).pipe(
      map((snapshot) => snapshot.data()),
      switchMap((productList: any) => {
        if (!productList || !productList.products?.length) return of([]);

        const observables$ = productList.products.map((productId: string) => {
          const collectionRef = collection(this.firestore, `private_syndicate_ticket_listing/${syndId}/${productId}`);
          const q = query(collectionRef);

          return from(getDocs(q)).pipe(
            map((querySnapshot) =>
              querySnapshot.docs.map((docSnap) => ({
                ...docSnap.data(),
                id: docSnap.id,
              }))
            )
          );
        });

        return combineLatest(observables$);
      })
    );
  }

  private extractDrawRound(docId: string, data: Data): number | null {
    if (data?.draw_round != null) {
      return Number(data.draw_round);
    }
    const roundMatch = docId.match(/_Round_(\d+)$/);
    return roundMatch ? Number(roundMatch[1]) : null;
  }

  getCheckedTickets(id: string, date: string) {
    if (!id || !date) {
      return of(null);
    }
    return this.catchService.isOnline$.pipe(
      tap((isOnline) => {
      }),
      switchMap((isOnline) => {
        if (!isOnline) {
          return of([]);
        }
        return this.authService.getUid$("SyndicateRepositoryService").pipe(
          switchMap((userId) => {
            if (!userId) {
              this.authService.errorHandlerLogs('No userId found in Firestore.');
              return of([]);
            }

            const path = `/users/${userId}/check_winnings/${id}/${date}`;
            const collectionRef = collection(this.firestore, path);
            const q = query(collectionRef);


            return from(getDocs(q)).pipe(
              map((querySnapshot) => {
                const results = querySnapshot.docs.map((docSnapshot) => {
                  const data = docSnapshot.data();
                  const drawRound = this.extractDrawRound(docSnapshot.id, data);
                  return {
                    ...data,
                    id: docSnapshot.id,
                    ...(drawRound != null ? { draw_round: drawRound } : {}),
                  };
                });
                return results.sort((a, b) => (a.draw_round ?? 0) - (b.draw_round ?? 0));
              }),
              this.catchService.safeCollectionRead(),
              catchError((error) => {
                this.authService.errorHandlerLogs('Error fetching checked tickets from Firestore:', error);
                return of([]);
              })
            );
          }));
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((tickets: any[]) => {
        const processed = tickets.map(ticket => ({
          ...ticket,
          showFlip: false,
          showLines: true,
          dotAnimation: false,
          viewText: true,
        }));
        return processed;
      })
    );
  }


  getCheckedTicketDetail(id) {
    if (!id) return of(null);
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.authService
          .getAngularFireStore(`/users/${userId}/check_winnings/${id}`, "snapshotChanges")
          .pipe(map((res: Data) => res?.payload?.data()));
      }));
  }

  checkUserPsStatus(id: string) {
    if (!id) return of(null);

    return this.catchService.isOnline$.pipe(
      switchMap(async (isOnline) => {
        if (!isOnline) return [];

        const collectionRef = collection(this.firestore, `/private_syndicate_member_listing/${id}/members`); // Reference Firestore collection
        const q = query(collectionRef); // Query the collection
        const querySnapshot = await getDocs(q); // Fetch documents

        return querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          memberId: doc.id, // Add memberId field to the data
        }));
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }

  getTriggerData(trigger) {
    if (!trigger) return of[""];
    return this.authService
      .getAngularFireStore(`${trigger}`, "snapshotChanges")
      .pipe(map((res: Data) => res?.payload?.data));
  }

  getSyndicateHoliday(psId: string) {
    return this.RequestSyndicateService.getSyndicatePaymentHolidayRequest(psId).pipe(
      switchMap((payload) => {
        if (!payload) {
          return of(null);
        }
        return this.authService.v2CloudFunctionResponse(
          this.callableFunction(payload)
        );
      })
    );
  }

  saveSyndicatPurchaseDetails(syndicate_id: string, purchaseDetail: any) {
    const products = [];
    if (!purchaseDetail || !purchaseDetail.products) return;
    purchaseDetail.products?.map((p) => {
      products.push({
        line_count: p?.line_count,
        product_name: p?.product_name,
        draw_day_group: p?.draw_day_group,
      });
    });

    purchaseDetail.products = products;
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const path_id = "WOvW5cx05m3px3D8UNiJ";
        const doc_data = purchaseDetail;
        const dv = { docId: syndicate_id };
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).pipe(
          catchError((err) => {
            this.authService.errorHandlerLogs('Failed to save syndicate purchase details:', err);
            return of(null);
          })
        );
      }),
      catchError(() => of(null))
    );
  }

  updateFirstTimePSPopup(sId: string, memberId: string) {
    if (!sId || !memberId) return;

    const path_id = "tsG0w9JMjE0gwGyrCTks";
    const doc_data = { firstTimeLoadDone: true };
    const dv = { docId: sId, memberId: memberId };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (err) => {
        this.authService.errorHandlerLogs('Error in memberId updatefirestoredoc', err);
        throw err;
      }
    });
  }

  updateNewMessageChat(psId: any) {
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId || !psId) return of(null);

        const path_id = "Qw5NvMI6j9LBEAfev0Q6";
        const doc_data = { new_message: false };
        const dv = { docId: psId };
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).pipe(
          catchError((error) => {
            this.authService.errorHandlerLogs("Error updating new_message:", error);
            return of(null);
          })
        );
      }),
      catchError(() => of(null))
    );
  }
  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): any[] {
    return actions.map((a) => {
      const data = a?.payload?.doc?.data() as any;
      const id = a?.payload?.doc?.id;
      return { id, ...data };
    });
  }
  getUserJacktpotTrigger() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of([]);
        return this.authService.getUid$("SyndicateRepositoryService").pipe(
          switchMap(async (userId) => {
            if (!userId) return [];
            const collectionRef = collection(this.firestore, `/users/${userId}/jackpot_trigger`); // Reference Firestore collection
            const q = query(collectionRef); // Query the collection
            const querySnapshot = await getDocs(q); // Fetch documents

            return querySnapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id, // Include document ID in the result
            }));
          })
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((actions: any[]) => actions)
    );
  }
  getSyndicatePending() {
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.authService
          .getAngularFireStore(`/users/${userId}`, "snapshotChanges")
          .pipe(
            map((res: Data) => {
              if (!res || typeof res?.data !== "function") return null;
              return res?.exists?.() ? res.data() : null;
            })
          );
      }));
  }

  getCheckWinningsStatsMessage() {
    return this.authService
      .getAngularFireStore(`/general_modules/check_winnings_stats_message`, "snapshotChanges")
      .pipe(
        map((res: Data) => res?.payload?.data()),
        shareReplay(1)
      );
  }

  getCurrentCheckWinningsStats() {
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) userId = null;
        const docRef = doc(this.firestore, `/users/${userId}/check_winnings_stats/current`); // Reference Firestore document
        return new Observable((observer) => {
          onSnapshot(
            docRef,
            (docSnap) => {
              const data = docSnap?.data(); // Extract document data
              observer.next(data);
            },
            (err) => {
              observer.error(err); // Handle errors
            }
          );
        }).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      }));

  }

  getOneTimeFeatures() {
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) userId = null;
        const docRef = doc(
          collection(
            doc(this.firestore, `users/${userId}`),
            `special_features`
          ),
          `enable_features_one_time`
        ); // Reference Firestore document
        return new Observable((observer) => {
          onSnapshot(
            docRef,
            (docSnap) => {
              const data = docSnap?.data(); // Extract document data
              observer.next(data);
            },
            (err) => {
              observer.error(err); // Handle errors
            }
          );
        }).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        ).pipe(
          shareReplay(1)
        );
      }));
  }

  updatePreviousCheckNodes(node: any) {
    if (!node) return of(null);
    return this.authService.getUid$("SyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        const path_id = "0ueEm5K4JyBL3un8b4K6";
        const doc_data = { [node]: false };
        const method = "update";
        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method
        }).pipe(
          catchError((error) => {
            this.authService.errorHandlerLogs("Error updating document:", error);
            return of(null);
          })
        );
      }),
      catchError(() => of(null))
    );
  }
  updateThreshold(updateTriggerData) {
    return this.authService.getV2CloudFunction(
      "updatejackpottrigger",
      this.RequestPaymentService.updateJackpotThreshold(updateTriggerData)
    );
  }
  // This function using to get badge details data to show achievement animation
  getSyndicateCustomerPurchaseDetails$: Observable<Data> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      return this.authService.getUid$('SyndicateRepositoryService').pipe(
        switchMap((userId) => {
          if (!userId) return of(null);

          const collectionRef = collection(this.firestore, `users/${userId}/customer_purchase`);
          const q = query(collectionRef, orderBy('timestamp_creation', 'desc'));

          // Convert Firestore promise to Observable
          return from(getDocs(q)).pipe(
            map((querySnapshot) => {
              const results = querySnapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
              }));
              return results.length > 0 ? results[0] : null;
            })
          );
        })
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  );

  // This method has been used to set isShown node to it
  userShownPurchaseStatus(syndicate_id: string) {
    try {
      const path_id = "WOvW5cx05m3px3D8UNiJ";
      const doc_data = { isShown: true };
      const dv = { docId: syndicate_id };
      const method = "update";

      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        dv,
        method,
      }).subscribe({
        error: (err) => {
          this.authService.errorHandlerLogs('Failed to update user shown purchase status:', err);
        }
      });
    } catch (err) {
      return err; // Return error if something goes wrong
    }
  }

  async closeAllDialogs() {
    const topModal = await this.modalCtrl?.getTop();
    await this.dismissAll(topModal);
  }

  dismissAll(modal: HTMLIonModalElement) {
    return new Promise((resolve) => {
      modal
        ?.dismiss()
        ?.then(async () => {
          const getNewTop = await this.modalCtrl?.getTop();
          if (getNewTop) {
            resolve(this.dismissAll(getNewTop));
          } else {
            resolve(true);
          }
        })
        .catch((error) => {
          return error;
        });
    });
  }

  ngOnDestroy() {
    if (this.syndicateMembersSub) {
      this.syndicateMembersSub.unsubscribe();
    }
  }

  getNextDrawDay(lotteryId: string): Observable<string | null> {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (isOnline) {
          const path_id = "XAtMaPS3TBkzfG4fPHHE";
          const dv = { docId: lotteryId };
          const method = "read";

          return this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            dv,
            method,
          }).pipe(map((snapshot: any) => snapshot?.data || null)
          );
        } else {
          return of(null);
        }
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((data: Data): string | null => {
        if (!data?.draw_day?.length) return null;

        const weekdayMap: Record<string, number> = {
          sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
          thursday: 4, friday: 5, saturday: 6,
        };

        const drawDays = data.draw_day.map((d: string) => d.toLowerCase());
        const today = new Date().getDay();

        const next = drawDays
          .map(day => ({ day, offset: (weekdayMap[day] - today + 7) % 7 }))
          .sort((a, b) => a.offset - b.offset)[0]?.day;

        return next ? next[0].toUpperCase() + next.slice(1) : null;
      })
    );
  }


}
